import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { parseISO, differenceInCalendarDays } from 'date-fns';
import { isSickLeave, isRegularLeave, isPreviousYearLeave } from '@/pages/blank-page/utils/leaveUtils';

export type Occurrence = {
  id: string;
  title: string;
  start_date: string;
  end_date: string;
  days: number;
};

export type NonWorkingStats = {
  base: {
    regular: number;
    previousYear: number;
    sick: number;
    noWork: number;
  };
  adjustments: {
    regular: number;
    previousYear: number;
    sick: number;
    noWork: number;
    notes?: string | null;
  };
  total: {
    regular: number;
    previousYear: number;
    sick: number;
    noWork: number;
  };
  ytd: {
    base: {
      regular: number;
      previousYear: number;
      sick: number;
      noWork: number;
    };
    total: {
      regular: number;
      previousYear: number;
      sick: number;
      noWork: number;
    };
  };
  occurrences: {
    regular: Occurrence[];
    previousYear: Occurrence[];
    sick: Occurrence[];
    noWork: Occurrence[];
  };
};

const isNoWork = (title?: string) => {
  if (!title) return false;
  const t = title.toUpperCase();
  return t.includes('ΜΗ ΕΡΓΑΣΙΑ') || t.includes('NO WORK');
};

const countDaysInclusive = (start: string, end: string) => {
  try {
    const s = parseISO(start);
    const e = parseISO(end);
    return Math.max(0, differenceInCalendarDays(e, s) + 1);
  } catch {
    return 0;
  }
};

const countOverlapInclusive = (startISO: string, endISO: string, rangeStart: Date, rangeEnd: Date) => {
  try {
    const s = parseISO(startISO);
    const e = parseISO(endISO);
    const start = s < rangeStart ? rangeStart : s;
    const end = e > rangeEnd ? rangeEnd : e;
    if (end < start) return 0;
    return differenceInCalendarDays(end, start) + 1;
  } catch {
    return 0;
  }
};

export const useNonWorkingStats = (params: { selectedGuardId?: string; year?: number; isAdmin?: boolean }) => {
  const { selectedGuardId, year = new Date().getFullYear(), isAdmin = false } = params;
  const queryClient = useQueryClient();

  const guardIdQuery = useQuery({
    queryKey: ['current-user-guard-id'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user?.id ?? null;
    },
  });

  const effectiveGuardId = selectedGuardId || (guardIdQuery.data ?? undefined);

  const statsQuery = useQuery<NonWorkingStats>({
    queryKey: ['non-working-stats', effectiveGuardId, year],
    enabled: !!effectiveGuardId,
    queryFn: async () => {
      const yearStart = `${year}-01-01`;
      const yearEnd = `${year}-12-31`;

      // Fetch schedules labeled as leave/non-work for this guard within the year
      let schedulesQuery = supabase
        .from('team_schedules')
        .select('id,title,start_date,end_date,assigned_guards')
        .gte('start_date', yearStart)
        .lte('end_date', yearEnd)
        .contains('assigned_guards', [effectiveGuardId]);

      // Titles likely in Greek/English variants
      schedulesQuery = schedulesQuery.or(
        [
          "title.ilike.%ΑΔΕΙΑ%",
          "title.ilike.%ΚΑΝΟΝΙΚ%",
          "title.ilike.%ΠΡΟΗΓ%",
          "title.ilike.%ΑΝΑΡΡΩ%",
          "title.ilike.%SICK%",
          "title.ilike.%REGULAR%",
          "title.ilike.%PREVIOUS%",
          "title.ilike.%NO WORK%",
          "title.ilike.%ΜΗ ΕΡΓΑΣΙΑ%",
        ].join(',')
      );

      const { data: schedules, error } = await schedulesQuery;
      if (error) throw error;

      const base = { regular: 0, previousYear: 0, sick: 0, noWork: 0 };
      const ytdBase = { regular: 0, previousYear: 0, sick: 0, noWork: 0 };
      const occurrences = {
        regular: [] as Occurrence[],
        previousYear: [] as Occurrence[],
        sick: [] as Occurrence[],
        noWork: [] as Occurrence[],
      };

      const yearStartDate = parseISO(yearStart);
      const yearEndDate = parseISO(yearEnd);
      let cutoff = new Date();
      if (cutoff > yearEndDate) cutoff = yearEndDate;
      if (cutoff < yearStartDate) cutoff = yearStartDate;

      for (const s of schedules ?? []) {
        const days = countDaysInclusive(s.start_date as string, s.end_date as string);
        const daysYtd = countOverlapInclusive(
          s.start_date as string,
          s.end_date as string,
          yearStartDate,
          cutoff
        );
        const title = (s.title as string) || '';
        const occ: Occurrence = {
          id: String(s.id),
          title,
          start_date: s.start_date as string,
          end_date: s.end_date as string,
          days,
        };
        if (isSickLeave(title)) {
          base.sick += days;
          ytdBase.sick += daysYtd;
          occurrences.sick.push(occ);
        } else if (isRegularLeave(title)) {
          base.regular += days;
          ytdBase.regular += daysYtd;
          occurrences.regular.push(occ);
        } else if (isPreviousYearLeave(title)) {
          base.previousYear += days;
          ytdBase.previousYear += daysYtd;
          occurrences.previousYear.push(occ);
        } else if (isNoWork(title)) {
          base.noWork += days;
          ytdBase.noWork += daysYtd;
          occurrences.noWork.push(occ);
        }
      }

      const { data: adjustment, error: adjErr } = await supabase
        .from('leave_adjustments')
        .select('*')
        .eq('guard_id', effectiveGuardId)
        .eq('year', year)
        .maybeSingle();
      if (adjErr && adjErr.code !== 'PGRST116') throw adjErr;

      const adjustments = {
        regular: adjustment?.regular_leave_delta ?? 0,
        previousYear: adjustment?.previous_year_leave_delta ?? 0,
        sick: adjustment?.sick_leave_delta ?? 0,
        noWork: adjustment?.no_work_delta ?? 0,
        notes: adjustment?.notes ?? null,
      };

      const total = {
        regular: base.regular + adjustments.regular,
        previousYear: base.previousYear + adjustments.previousYear,
        sick: base.sick + adjustments.sick,
        noWork: base.noWork + adjustments.noWork,
      };

      const ytdTotal = {
        regular: ytdBase.regular + adjustments.regular,
        previousYear: ytdBase.previousYear + adjustments.previousYear,
        sick: ytdBase.sick + adjustments.sick,
        noWork: ytdBase.noWork + adjustments.noWork,
      };

      return { base, adjustments, total, ytd: { base: ytdBase, total: ytdTotal }, occurrences };
    },
  });

  const saveMutation = useMutation({
    mutationKey: ['save-leave-adjustments', effectiveGuardId, year],
    mutationFn: async (payload: { regular: number; previousYear: number; sick: number; noWork: number; notes?: string }) => {
      if (!effectiveGuardId) throw new Error('Guard is not selected');
      const { regular, previousYear, sick, noWork, notes } = payload;
      const { error } = await supabase
        .from('leave_adjustments')
        .upsert(
          {
            guard_id: effectiveGuardId,
            year,
            regular_leave_delta: regular,
            previous_year_leave_delta: previousYear,
            sick_leave_delta: sick,
            no_work_delta: noWork,
            notes: notes ?? null,
          },
          { onConflict: 'guard_id,year' }
        );
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['non-working-stats', effectiveGuardId, year] });
    },
  });

  return {
    effectiveGuardId,
    statsQuery,
    saveMutation,
  };
};
